import { AlertTriangle, UserCheck } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { PortalAccessGuard } from '@/modulos/portal-credenciado/comum/PortalAccessGuard';
import { alertaDeValidade, formatarDia } from '@/modulos/portal-credenciado/comum/vencimento';
import { mascaraCpfCnpj, mascaraTelefone } from '@/nucleo/formato/documentos';
import { DetailHeader } from '@/ui/DetailHeader';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { CardSkeleton } from '@/ui/base/table-skeleton';

import { useCondutor } from './dados';
import type { Condutor } from './contrato';

const VOLTAR = {
  backTo: '/credenciado/condutores',
  backLabel: 'Meus Condutores',
} as const;

/**
 * Selo de validade do cartão "Cursos e Documentos" (`dateBadge` do
 * protótipo): vermelho com alerta se venceu, cinza com alerta se vence em
 * menos de 30 dias, contorno simples fora disso. Sem data, nenhum selo.
 */
function SeloDeValidade({ rotulo, validade }: { rotulo: string; validade: string | null }) {
  if (!validade) {
    return null;
  }
  const alerta = alertaDeValidade(validade);
  const variante =
    alerta === 'vencido' ? 'destructive' : alerta === 'vencendo' ? 'secondary' : 'outline';
  return (
    <Badge variant={variante} className="gap-1 text-[11px]">
      {alerta && (
        <AlertTriangle
          className={alerta === 'vencendo' ? 'h-3 w-3 text-warning' : 'h-3 w-3'}
          role="img"
          aria-label={alerta === 'vencido' ? 'Vencido' : 'Vence em breve'}
        />
      )}
      {rotulo}: {formatarDia(validade)}
    </Badge>
  );
}

function CursosEDocumentos({ condutor }: { condutor: Condutor }) {
  const semCurso =
    !condutor.curso_primeiro_socorros &&
    !condutor.curso_transporte_escolar &&
    !condutor.curso_mobilidade_reduzida;

  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="text-base">
          <h2>Cursos e Documentos</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-2">
          {condutor.curso_primeiro_socorros && <Badge variant="outline">Primeiros Socorros</Badge>}
          {condutor.curso_transporte_escolar && <Badge variant="outline">Transporte Escolar</Badge>}
          {condutor.curso_mobilidade_reduzida && (
            <Badge variant="outline">Mobilidade Reduzida</Badge>
          )}
          {semCurso && <p className="text-xs text-muted-foreground">Nenhum curso registrado.</p>}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <SeloDeValidade rotulo="CNH" validade={condutor.cnh_validade} />
          <SeloDeValidade rotulo="CRMC" validade={condutor.crmc_validade} />
          <SeloDeValidade
            rotulo="1ºs Socorros"
            validade={condutor.curso_primeiro_socorros_validade}
          />
          <SeloDeValidade
            rotulo="Transp. Escolar"
            validade={condutor.curso_transporte_escolar_validade}
          />
          <SeloDeValidade rotulo="Mob. Reduzida" validade={condutor.curso_mobilidade_validade} />
          <SeloDeValidade
            rotulo="Atestado Médico"
            validade={condutor.data_vencimento_atestado_medico}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * `/credenciado/condutores/:slug` — detalhe de um condutor vinculado ao
 * credenciado logado, fiel a `pages/portal/PortalCondutorDetail.tsx` do
 * protótipo: cartão de identificação com o nome como `h1`, depois Habilitação
 * (CNH), CRMC e Cursos e Documentos, e a nota de somente leitura no rodapé.
 *
 * Dado mockado — ver `dados.ts` e `comum/mock.ts` para a justificativa. Slug
 * que não bate com nenhum condutor do mock cai no estado de "não encontrado"
 * (não num crash nem numa tela em branco). Diferente do protótipo, o "não
 * encontrado" mantém o "voltar": sem ele a tela não tem saída.
 *
 * Exclusiva de credenciado coletivo (PJ ou Cooperativa), pela guarda
 * `requirePJ`, igual ao protótipo.
 */
export function CondutorDetailPage() {
  return (
    <PortalAccessGuard regra="requirePJ">
      <DetalheDoCondutor />
    </PortalAccessGuard>
  );
}

function DetalheDoCondutor() {
  const { slug } = useParams<{ slug: string }>();
  const { data: condutor, isLoading, isError, refetch } = useCondutor(slug);

  if (isLoading) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <div role="status" aria-live="polite" className="space-y-6">
          <CardSkeleton />
          <CardSkeleton />
          <p className="sr-only">Carregando os dados do condutor.</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar este condutor agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      </div>
    );
  }

  if (!condutor) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <EmptyState
          icon={UserCheck}
          title="Condutor não encontrado"
          description="O registro solicitado não existe ou não está vinculado ao seu credenciamento."
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <DetailHeader {...VOLTAR} />

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold">{condutor.nome}</h1>
              <p className="mt-1 font-mono text-sm text-muted-foreground">
                {mascaraCpfCnpj(condutor.cpf)}
              </p>
              {condutor.email && (
                <p className="break-all text-sm text-muted-foreground">{condutor.email}</p>
              )}
              {condutor.telefone && (
                <p className="text-sm text-muted-foreground">
                  {mascaraTelefone(condutor.telefone)}
                </p>
              )}
            </div>
            <StatusBadge status={condutor.situacao} label={condutor.situacao} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="text-base">
            <h2>Habilitação (CNH)</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Número</p>
            <p className="font-mono">{condutor.cnh_numero ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Categoria</p>
            <p>{condutor.cnh_categoria ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Validade</p>
            <p>{formatarDia(condutor.cnh_validade)}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="text-base">
            <h2>CRMC</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs text-muted-foreground">Número</p>
            <p className="font-mono">{condutor.crmc_numero ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Validade</p>
            <p>{formatarDia(condutor.crmc_validade)}</p>
          </div>
        </CardContent>
      </Card>

      <CursosEDocumentos condutor={condutor} />

      <p className="text-xs text-muted-foreground">
        Dados em modo somente leitura. Para alterações cadastrais ou substituição, abra uma{' '}
        <span className="font-medium">solicitação de serviço</span> no menu Solicitações.
      </p>
    </div>
  );
}

export default CondutorDetailPage;
